import { useState, useEffect } from 'react'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { WATERMARK_POSITIONS } from '@/lib/constants'
import { Smile, ScanFace, Image, Scissors, FileText, Upload, Info } from 'lucide-react'
import { uploadFile } from '@/lib/utils'

function SliderRow({ label, value, min, max, step = 1, onChange }: {
  label: string; value: number; min: number; max: number; step?: number; onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-32 shrink-0 text-sm text-muted-foreground">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="w-12 shrink-0 rounded-md bg-muted px-2 py-0.5 text-center text-sm font-mono">{value}</span>
    </div>
  )
}

function ToggleRow({ label, description, checked, onChange, disabled }: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label 
      className={`flex items-start gap-3 rounded-lg border border-border bg-background px-4 py-3 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed border-dashed' : 'cursor-pointer hover:bg-muted'}`}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault()
          alert('Coming soon!')
        }
      }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => !disabled && onChange(e.target.checked)} className="mt-0.5" disabled={disabled} />
      <div>
        <div className="text-sm font-medium flex items-center gap-2">
          {label}
          {disabled && <span className="text-[10px] font-bold uppercase text-muted-foreground/60 bg-muted/80 px-1.5 py-0.5 rounded">Coming Soon</span>}
        </div>
        {description && <div className="text-xs text-muted-foreground mt-0.5">{description}</div>}
      </div>
    </label>
  )
}

function FileUploadRow({ label, value, accept, onFile, t }: { label: string; value?: string | null; accept: string; onFile: (url: string) => void; t: any }) {
  return (
    <div className="space-y-2">
      <label className="block">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-muted-foreground">{label}</span>
          {value && <span className="text-xs text-green-400 flex items-center gap-1"><span className="status-dot-green" /> {t('common.success')}</span>}
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-background px-4 py-3 hover:border-primary/40 hover:bg-muted cursor-pointer transition-colors">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{value ? t('branding.replaceLogo') : t('branding.uploadLogo')}</span>
          <input type="file" accept={accept} className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]
            if (f) {
              const url = await uploadFile(f)
              if (url) onFile(url)
            }
          }} />
        </div>
      </label>
      {value && accept.includes('image') && <img src={value} alt="" className="h-16 rounded-lg object-contain border border-border" />}
    </div>
  )
}

export default function EffectsSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const { effects } = event
  const [propsList, setPropsList] = useState<string[]>([])

  useEffect(() => {
    fetch('/api/props')
      .then(res => res.json())
      .then(data => setPropsList(data))
      .catch(err => console.error('Failed to fetch props:', err))
  }, [])

  const update = (key: keyof typeof effects, value: unknown) => {
    onChange({ effects: { ...effects, [key]: value } })
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold">{t('editor.effects')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('effects.settingsDesc')}</p>
      </div>

      {/* Note about implementation */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm">
        <Info className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-amber-300/80">{t('effects.canvasNote')}</div>
      </div>

      {/* Beauty Filter */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Smile className="h-4 w-4 text-muted-foreground" /> {t('effects.beautyFilter')}
        </div>
        <ToggleRow
          label={t('effects.enableBeauty')}
          description={t('effects.beautyDesc')}
          checked={effects.beautyFilter.enabled}
          onChange={(v) => update('beautyFilter', { ...effects.beautyFilter, enabled: v })}
          disabled={true}
        />
        {effects.beautyFilter.enabled && (
          <SliderRow label={t('effects.strength')} value={effects.beautyFilter.strength} min={0} max={1} step={0.05} onChange={(v) => update('beautyFilter', { ...effects.beautyFilter, strength: v })} />
        )}
      </div>

      {/* Watermark */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Image className="h-4 w-4 text-primary" /> {t('effects.watermark')}
        </div>
        <ToggleRow
          label={t('effects.enableWatermark')}
          description={t('effects.watermarkDesc')}
          checked={effects.watermark.enabled}
          onChange={(v) => update('watermark', { ...effects.watermark, enabled: v })}
          disabled={true}
        />
        {effects.watermark.enabled && (
          <>
            <FileUploadRow
              label={t('effects.watermarkAsset')}
              value={effects.watermark.asset}
              accept="image/*"
              onFile={(url) => update('watermark', { ...effects.watermark, asset: url })}
              t={t}
            />
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">{t('effects.position')}</div>
              <div className="grid grid-cols-3 gap-1.5">
                {WATERMARK_POSITIONS.map((p) => (
                  <button
                    key={p}
                    onClick={() => update('watermark', { ...effects.watermark, position: p })}
                    className={`rounded-lg border py-1.5 text-xs font-medium capitalize transition-all ${
                      effects.watermark.position === p
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    {p.replace('-', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <SliderRow label={t('effects.opacity')} value={effects.watermark.opacity} min={0} max={1} step={0.05} onChange={(v) => update('watermark', { ...effects.watermark, opacity: v })} />
          </>
        )}
      </div>

      {/* Green Screen */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Scissors className="h-4 w-4 text-emerald-400" /> {t('effects.greenScreen')}
        </div>
        <ToggleRow
          label={t('effects.enableGreenScreen')}
          description={t('effects.greenScreenDesc')}
          checked={effects.greenScreen.enabled}
          onChange={(v) => update('greenScreen', { ...effects.greenScreen, enabled: v })}
          disabled={true}
        />
        {effects.greenScreen.enabled && (
          <>
            <FileUploadRow
              label={t('effects.replacementBg')}
              value={effects.greenScreen.backgroundAsset}
              accept="image/*"
              onFile={(url) => update('greenScreen', { ...effects.greenScreen, backgroundAsset: url })}
              t={t}
            />
            <SliderRow label={t('effects.threshold')} value={effects.greenScreen.threshold} min={0} max={255} onChange={(v) => update('greenScreen', { ...effects.greenScreen, threshold: v })} />
          </>
        )}
      </div>

      {/* Disclaimer */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <FileText className="h-4 w-4 text-muted-foreground" /> {t('effects.disclaimer')}
        </div>
        <ToggleRow
          label={t('effects.showDisclaimer')}
          description={t('effects.disclaimerDesc')}
          checked={!!effects.disclaimer?.enabled}
          onChange={(v) => update('disclaimer', v ? { enabled: true, text: effects.disclaimer?.text || '' } : null)}
          disabled={true}
        />
        {effects.disclaimer && (
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Disclaimer text</div>
            <textarea
              value={effects.disclaimer.text}
              onChange={(e) => update('disclaimer', { ...effects.disclaimer, text: e.target.value })}
              placeholder="Enter disclaimer text…"
              className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground resize-none focus:border-primary/50 transition-colors"
              rows={3}
            />
          </div>
        )}
      </div>



      {/* Face Tracking AI */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <ScanFace className="h-4 w-4 text-muted-foreground" /> Face tracking (beta)
        </div>
        <ToggleRow
          label="Enable Face Tracking"
          description="Pre-enable AR face tracking props for this event."
          checked={effects.faceTracking?.enabled || false}
          onChange={(v) => update('faceTracking', { ...effects.faceTracking, enabled: v })}
        />
        {effects.faceTracking?.enabled && (
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground mb-1">Default Prop</div>
            <select 
              value={effects.faceTracking?.defaultProp || ''} 
              onChange={(e) => update('faceTracking', { ...effects.faceTracking, defaultProp: e.target.value })} 
              className="w-full p-2 border rounded-md bg-background text-foreground text-sm"
            >
              <option value="">None</option>
              {propsList.map(prop => (
                <option key={prop} value={`/props/${prop}`}>{prop}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}
